import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Folder,
  File,
  FolderPlus,
  Upload,
  Search,
  ChevronRight,
  Home,
  Download,
  Trash2,
  Edit3,
  Users,
  MoreVertical,
  Grid,
  List,
  AlertCircle,
  Shield,
  Lock,
  Globe,
  X,
  RefreshCw,
  UserX,
} from "lucide-react";
import MainHeader from "../MainHeader";
import useEdrive from "../hooks/useEdrive";
import useIAM from "../hooks/useIam";
import CreateFolderModal from "../modal/edrive/CreateFolderModal";
import RenameFolderModal from "../modal/edrive/RenameFolderModal";
import RenameFileModal from "../modal/edrive/RenameFileModal";
import UploadModal from "../modal/edrive/UploadModal";
import SearchModal from "../modal/edrive/SearchModal";
import PermissionModal from "../modal/edrive/PermissionModal";
import FileViewerModal from "../modal/edrive/FileViewerModal";
import InviteFolderModal from "../modal/edrive/InviteFolderModal";
import BanFolderModal from "../modal/edrive/BanFolderModal";
import DropdownMenu from "../modal/iam/DropdownMenu";
import FileIcon, { SimpleFileIcon } from "../elements/FileIcon";

const EDriveDashboard = () => {
  const {
    fetchPermissionList,
    updatePermission,
    createFolder,
    updateFolder,
    deleteFolder,
    fetchRootFolders,
    fetchSubFolder,
    fetchFolderInvitedMembers,
    inviteFolder,
    banFolder,
    uploadFiles,
    renameFile,
    deleteFile,
    viewFile,
    searchFiles,
  } = useEdrive();

  const { fetchIAMList } = useIAM();

  // 상태 관리
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolderType, setCurrentFolderType] = useState(null); // 현재 폴더 타입
  const [breadcrumb, setBreadcrumb] = useState([
    { id: null, name: "내 드라이브" },
  ]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // IAM 관련 상태
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedFolderForInvite, setSelectedFolderForInvite] = useState(null);
  const [selectedFolderForBan, setSelectedFolderForBan] = useState(null);
  const [iamMembers, setIamMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [invitedMembers, setInvitedMembers] = useState([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);

  // 모달 상태
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showRenameFileModal, setShowRenameFileModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showFileViewerModal, setShowFileViewerModal] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [createFolderForm, setCreateFolderForm] = useState({
    folder_name: "",
    folder_type: "private",
  });
  const [renameForm, setRenameForm] = useState({ name: "" });
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  // Refs
  const dropdownButtonRefs = useRef({});
  const fileInputRef = useRef(null);

  // 유틸리티 함수
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("ko-KR");
  };

  const getFileExtension = (file) => {
    return file.file_name.split(".").pop().toLowerCase();
  };

  // 데이터 로드
  const loadFolderContent = useCallback(
    async (folderId = null) => {
      setLoading(true);
      setErrorMsg("");

      if (!folderId) {
        // 루트 폴더
        const res = await fetchRootFolders();
        if (!res.ok) {
          setErrorMsg("폴더 목록을 불러오지 못했습니다.");
          setFolders([]);
          setFiles([]);
        } else {
          setFolders(res.folders);
          setFiles([]);
        }
      } else {
        // 서브 폴더
        const res = await fetchSubFolder(folderId);
        if (!res.ok) {
          setErrorMsg("폴더 내용을 불러오지 못했습니다.");
          setFolders([]);
          setFiles([]);
        } else {
          setFolders(res.folders || []);
          setFiles(res.files || []);
        }
      }

      setLoading(false);
      setIsRefreshing(false);
    },
    [fetchRootFolders, fetchSubFolder]
  );

  const loadPermissions = useCallback(async () => {
    setIsLoadingPermissions(true);
    const res = await fetchPermissionList();
    if (res.ok && !res.denied) {
      setPermissions(res.permissions);
    }
    setIsLoadingPermissions(false);
  }, [fetchPermissionList]);

  // IAM 멤버 목록 로드
  const loadIAMMembers = useCallback(async () => {
    const res = await fetchIAMList();
    if (res.ok) {
      setIamMembers(res.members);
      const me = res.members.find((m) => m.is_me);
      if (me) {
        setCurrentUser({
          user_id: me.user_id,
          user_name: me.user_name,
          grade: me.grade,
        });
      }
    }
  }, [fetchIAMList]);

  // 초대된 멤버 목록 로드 (특정 폴더)
  const loadInvitedMembers = useCallback(
    async (folderId) => {
      setIsLoadingInvites(true);
      try {
        const res = await fetchFolderInvitedMembers(folderId);
        if (res.ok && !res.nonSelector) {
          setInvitedMembers(res.members);
        } else {
          setInvitedMembers([]);
        }
      } catch (err) {
        console.error("초대 멤버 로드 실패:", err);
        setInvitedMembers([]);
      }
      setIsLoadingInvites(false);
    },
    [fetchFolderInvitedMembers]
  );

  // Effects
  useEffect(() => {
    const savedPath = sessionStorage.getItem("edrivePath");
    if (savedPath && !isInitialized) {
      try {
        const pathData = JSON.parse(savedPath);
        setBreadcrumb(pathData.breadcrumb);
        setCurrentFolderId(pathData.currentFolderId);
        setCurrentFolderType(pathData.currentFolderType);
        setIsInitialized(true);
      } catch (e) {
        console.error("Failed to restore path:", e);
        setIsInitialized(true);
      }
    } else {
      setIsInitialized(true);
    }
  }, [isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      sessionStorage.setItem(
        "edrivePath",
        JSON.stringify({
          breadcrumb,
          currentFolderId,
          currentFolderType,
        })
      );
    }
  }, [breadcrumb, currentFolderId, currentFolderType, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      loadFolderContent(currentFolderId);
    }
  }, [currentFolderId, isInitialized]); // loadFolderContent를 dependency에서 제거

  useEffect(() => {
    loadIAMMembers();
  }, []); // dependency를 빈 배열로 변경하여 한 번만 실행

  useEffect(() => {
    if (infoMsg) {
      const timer = setTimeout(() => setInfoMsg(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [infoMsg]);

  useEffect(() => {
    if (errorMsg) {
      const timer = setTimeout(() => setErrorMsg(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  // 핸들러
  const handleRefresh = () => {
    setIsRefreshing(true);
    loadFolderContent(currentFolderId);
  };

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderType(folder.folder_type);
    setBreadcrumb([
      ...breadcrumb,
      {
        id: folder.id,
        name: folder.folder_name,
        folder_type: folder.folder_type,
      },
    ]);
    setSelectedItems(new Set());
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newBreadcrumb);
    const targetFolder = newBreadcrumb[newBreadcrumb.length - 1];
    setCurrentFolderId(targetFolder.id);
    setCurrentFolderType(targetFolder.folder_type || null);
    setSelectedItems(new Set());
  };

  const handleCreateFolder = async () => {
    if (!createFolderForm.folder_name.trim()) {
      setErrorMsg("폴더 이름을 입력하세요.");
      return;
    }

    const success = await createFolder({
      folder_name: createFolderForm.folder_name,
      parent_folder_id: currentFolderId,
      folder_type: createFolderForm.folder_type,
    });

    if (success) {
      setShowCreateFolderModal(false);
      setCreateFolderForm({ folder_name: "", folder_type: "private" });
      setInfoMsg("폴더가 생성되었습니다.");
      loadFolderContent(currentFolderId);
    } else {
      setErrorMsg("폴더 생성에 실패했습니다.");
    }
  };

  const handleRenameFolder = async () => {
    if (!renameForm.name.trim()) {
      setErrorMsg("폴더 이름을 입력하세요.");
      return;
    }

    const success = await updateFolder({
      folder_id: renameTarget.id,
      folder_name: renameForm.name,
    });

    if (success) {
      setShowRenameFolderModal(false);
      setRenameTarget(null);
      setRenameForm({ name: "" });
      setInfoMsg("폴더 이름이 변경되었습니다.");
      loadFolderContent(currentFolderId);
    } else {
      setErrorMsg("폴더 이름 변경에 실패했습니다.");
    }
  };

  const handleRenameFile = async () => {
    if (!renameForm.name.trim()) {
      setErrorMsg("파일 이름을 입력하세요.");
      return;
    }

    const success = await renameFile(renameTarget.id, renameForm.name);

    if (success) {
      setShowRenameFileModal(false);
      setRenameTarget(null);
      setRenameForm({ name: "" });
      setInfoMsg("파일 이름이 변경되었습니다.");
      loadFolderContent(currentFolderId);
    } else {
      setErrorMsg("파일 이름 변경에 실패했습니다.");
    }
  };

  const handleDeleteFolder = async (folder) => {
    const yes = window.confirm(
      `"${folder.folder_name}" 폴더를 삭제하시겠습니까?\n폴더 내 모든 파일과 하위 폴더가 삭제됩니다.`
    );
    if (!yes) return;

    const res = await deleteFolder(folder.id);
    if (res.denied) {
      setInfoMsg("폴더 삭제 권한이 없습니다.");
      return;
    }
    if (!res.ok) {
      setErrorMsg("폴더 삭제에 실패했습니다.");
      return;
    }

    setInfoMsg("폴더가 삭제되었습니다.");
    setDropdownOpen(null);
    loadFolderContent(currentFolderId);
  };

  const handleDeleteFile = async (file) => {
    const yes = window.confirm(`"${file.file_name}" 파일을 삭제하시겠습니까?`);
    if (!yes) return;

    const res = await deleteFile(file.id);
    if (res.denied) {
      setInfoMsg("파일 삭제 권한이 없습니다.");
      return;
    }
    if (!res.ok) {
      setErrorMsg("파일 삭제에 실패했습니다.");
      return;
    }

    setInfoMsg("파일이 삭제되었습니다.");
    setDropdownOpen(null);
    loadFolderContent(currentFolderId);
  };

  const handleFileUpload = async () => {
    if (!currentFolderId) {
      setErrorMsg(
        "루트 폴더에는 파일을 업로드할 수 없습니다. 먼저 폴더를 생성하거나 선택해주세요."
      );
      setShowUploadModal(false);
      return;
    }

    if (uploadingFiles.length === 0) {
      setErrorMsg("업로드할 파일을 선택하세요.");
      return;
    }

    const res = await uploadFiles(uploadingFiles, currentFolderId);
    if (res.empty) {
      setErrorMsg("업로드할 파일이 없습니다.");
      return;
    }
    if (!res.ok) {
      setErrorMsg("파일 업로드에 실패했습니다.");
      return;
    }

    if (res.failedFiles.length > 0) {
      setErrorMsg(`일부 파일 업로드 실패: ${res.failedFiles.join(", ")}`);
    } else {
      setInfoMsg("파일이 업로드되었습니다.");
    }

    setShowUploadModal(false);
    setUploadingFiles([]);
    loadFolderContent(currentFolderId);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    const res = await searchFiles(searchQuery);
    if (!res.ok) {
      setErrorMsg("검색에 실패했습니다.");
      return;
    }

    setSearchResults(res.result);
  };

  const handlePermissionUpdate = useCallback(
    async (userId, accessLevel) => {
      const res = await updatePermission(userId, accessLevel);
      if (res.denied) {
        setInfoMsg("권한 수정 권한이 없습니다. (마스터 전용)");
        return;
      }
      if (!res.ok) {
        setErrorMsg("권한 수정에 실패했습니다.");
        return;
      }

      setInfoMsg("권한이 수정되었습니다.");
      loadPermissions();
    },
    [updatePermission, loadPermissions]
  );

  const handleFileView = (file) => {
    setViewingFile(file);
    setShowFileViewerModal(true);
  };

  const handleFileDownload = async (file) => {
    try {
      const blob = await viewFile(file.id);
      if (blob) {
        const ext = getFileExtension(file);
        const mimeTypes = {
          pdf: "application/pdf",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          gif: "image/gif",
          bmp: "image/bmp",
          svg: "image/svg+xml",
          webp: "image/webp",
          txt: "text/plain",
          md: "text/markdown",
          log: "text/plain",
          json: "application/json",
          xml: "application/xml",
          csv: "text/csv",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          xls: "application/vnd.ms-excel",
          xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          ppt: "application/vnd.ms-powerpoint",
          pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          zip: "application/zip",
          rar: "application/x-rar-compressed",
          "7z": "application/x-7z-compressed",
          mp4: "video/mp4",
          mp3: "audio/mpeg",
          wav: "audio/wav",
          hwp: "application/x-hwp",
          hwpx: "application/x-hwpx",
          odt: "application/vnd.oasis.opendocument.text",
          ods: "application/vnd.oasis.opendocument.spreadsheet",
          odp: "application/vnd.oasis.opendocument.presentation",
        };
        const mimeType = mimeTypes[ext] || "application/octet-stream";

        const typedBlob = new Blob([blob], { type: mimeType });
        const url = URL.createObjectURL(typedBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (err) {
      console.error("다운로드 오류:", err);
      setErrorMsg("파일 다운로드에 실패했습니다.");
    }
  };

  // 초대 핸들러
  const handleInviteFolder = useCallback(
    async (inviteId, folderId) => {
      const success = await inviteFolder(inviteId, folderId);
      if (success) {
        setInfoMsg("멤버가 폴더에 초대되었습니다.");
        // 초대 후 초대된 멤버 목록 다시 로드
        if (
          folderId === currentFolderId ||
          folderId === selectedFolderForInvite?.id
        ) {
          loadInvitedMembers(folderId);
        }
        return true;
      } else {
        setErrorMsg("멤버 초대에 실패했습니다.");
        return false;
      }
    },
    [inviteFolder, currentFolderId, selectedFolderForInvite, loadInvitedMembers]
  );

  // 추방 핸들러
  const handleBanFolder = useCallback(
    async (banId, folderId) => {
      const success = await banFolder(banId, folderId);
      if (success) {
        setInfoMsg("멤버가 폴더에서 추방되었습니다.");
        // 추방 후 초대된 멤버 목록 다시 로드
        if (
          folderId === currentFolderId ||
          folderId === selectedFolderForBan?.id
        ) {
          loadInvitedMembers(folderId);
        }
        return true;
      } else {
        setErrorMsg("멤버 추방에 실패했습니다.");
        return false;
      }
    },
    [banFolder, currentFolderId, selectedFolderForBan, loadInvitedMembers]
  );

  // 컴포넌트
  const Banner = ({ type = "info", message }) => {
    if (!message) return null;
    const cls =
      type === "error"
        ? "bg-red-50 text-red-800 border-red-200"
        : "bg-blue-50 text-blue-800 border-blue-200";
    const Icon = type === "error" ? AlertCircle : Shield;
    return (
      <div
        className={`mb-4 border ${cls} rounded-md px-3 py-2 text-sm flex items-center gap-2`}
      >
        <Icon className="w-4 h-4" />
        <span>{message}</span>
      </div>
    );
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <Folder className="w-16 h-16 text-gray-300 mb-4" />
      <p className="text-gray-500 text-lg">폴더가 비어있습니다</p>
      <p className="text-gray-400 text-sm mt-2">
        폴더를 생성하거나 파일을 업로드하세요
      </p>
    </div>
  );

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gray-50">
        {/* 헤더 */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-xl font-medium text-gray-900 flex items-center gap-2">
                <Home className="w-5 h-5" />
                EDrive
              </h1>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className={`flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors ${
                    isRefreshing ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  새로고침
                </button>
                <button
                  onClick={() => {
                    setShowPermissionModal(true);
                    loadPermissions();
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  권한 관리
                </button>
                <button
                  onClick={() => setShowSearchModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <Search className="w-4 h-4" />
                  검색
                </button>
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <FolderPlus className="w-4 h-4" />새 폴더
                </button>
                <button
                  onClick={() => {
                    if (!currentFolderId) {
                      setErrorMsg(
                        "루트 폴더에는 파일을 업로드할 수 없습니다. 먼저 폴더를 생성하거나 선택해주세요."
                      );
                      return;
                    }
                    setShowUploadModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  업로드
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <Banner type="info" message={infoMsg} />
          <Banner type="error" message={errorMsg} />

          {/* 브레드크럼 & 뷰 모드 */}
          <div className="flex justify-between items-center mb-4">
            <nav className="flex items-center space-x-2 text-sm">
              {breadcrumb.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className={`hover:text-blue-600 transition-colors ${
                      index === breadcrumb.length - 1
                        ? "text-gray-900 font-medium"
                        : "text-gray-600"
                    }`}
                  >
                    {item.name}
                  </button>
                </React.Fragment>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === "grid"
                    ? "bg-gray-200 text-gray-900"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === "list"
                    ? "bg-gray-200 text-gray-900"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 메인 컨텐츠 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 min-h-[500px]">
            {loading && !isRefreshing ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-gray-500">로딩 중...</div>
              </div>
            ) : folders.length === 0 && files.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                {/* 공유 폴더일 때 공유하기 버튼 표시 */}
                {currentFolderId && currentFolderType === "share" && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-gray-600">공유 폴더</span>
                      </div>
                      <button
                        onClick={async () => {
                          // 현재 폴더의 초대된 멤버 로드
                          await loadInvitedMembers(currentFolderId);
                          const currentFolderInfo =
                            breadcrumb[breadcrumb.length - 1];
                          setSelectedFolderForInvite({
                            id: currentFolderId,
                            folder_name: currentFolderInfo.name,
                            folder_type: "share",
                          });
                          setShowInviteModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <Users className="w-4 h-4" />
                        폴더 공유하기
                      </button>
                    </div>
                  </div>
                )}

                {viewMode === "grid" ? (
                  <div className="p-6">
                    {/* 폴더 섹션 */}
                    {folders.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-sm font-medium text-gray-700 mb-4">
                          폴더
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          {folders.map((folder) => (
                            <div key={folder.id} className="relative group">
                              <button
                                onClick={() => handleFolderClick(folder)}
                                className="w-full p-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                              >
                                <div className="flex flex-col items-center gap-2">
                                  <div className="relative">
                                    <Folder className="w-12 h-12 text-blue-500" />
                                    {folder.folder_type === "share" && (
                                      <Globe className="w-4 h-4 text-green-600 absolute -bottom-1 -right-1" />
                                    )}
                                  </div>
                                  <span className="text-sm text-gray-700 truncate w-full text-center">
                                    {folder.folder_name}
                                  </span>
                                </div>
                              </button>

                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  ref={(el) =>
                                    (dropdownButtonRefs.current[
                                      `folder-${folder.id}`
                                    ] = el)
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDropdownOpen(
                                      dropdownOpen === `folder-${folder.id}`
                                        ? null
                                        : `folder-${folder.id}`
                                    );
                                  }}
                                  className="p-1 rounded-md bg-white shadow-md hover:bg-gray-50"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>

                                <DropdownMenu
                                  isOpen={
                                    dropdownOpen === `folder-${folder.id}`
                                  }
                                  onClose={() => setDropdownOpen(null)}
                                  buttonRef={
                                    dropdownButtonRefs.current[
                                      `folder-${folder.id}`
                                    ]
                                      ? {
                                          current:
                                            dropdownButtonRefs.current[
                                              `folder-${folder.id}`
                                            ],
                                        }
                                      : null
                                  }
                                >
                                  {folder.folder_type === "share" && (
                                    <>
                                      <button
                                        onClick={async () => {
                                          await loadInvitedMembers(folder.id);
                                          setSelectedFolderForInvite(folder);
                                          setShowInviteModal(true);
                                          setDropdownOpen(null);
                                        }}
                                        className="flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full text-left"
                                      >
                                        <Users className="w-4 h-4 mr-2" />
                                        멤버 초대
                                      </button>
                                      <button
                                        onClick={async () => {
                                          await loadInvitedMembers(folder.id);
                                          setSelectedFolderForBan(folder);
                                          setShowBanModal(true);
                                          setDropdownOpen(null);
                                        }}
                                        className="flex items-center px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 w-full text-left"
                                      >
                                        <UserX className="w-4 h-4 mr-2" />
                                        멤버 추방
                                      </button>
                                      <div className="border-t border-gray-100 my-1" />
                                    </>
                                  )}
                                  <button
                                    onClick={() => {
                                      setRenameTarget(folder);
                                      setRenameForm({
                                        name: folder.folder_name,
                                      });
                                      setShowRenameFolderModal(true);
                                      setDropdownOpen(null);
                                    }}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                  >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    이름 변경
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFolder(folder)}
                                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    삭제
                                  </button>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 파일 섹션 */}
                    {files.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-4">
                          파일
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                          {files.map((file) => (
                            <div key={file.id} className="relative group">
                              <button
                                onClick={() => handleFileView(file)}
                                className="w-full p-4 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                              >
                                <div className="flex flex-col items-center gap-2">
                                  <FileIcon type={file.file_type} size={48} />
                                  <span className="text-sm text-gray-700 truncate w-full text-center">
                                    {file.file_name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatFileSize(file.file_size)}
                                  </span>
                                </div>
                              </button>

                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  ref={(el) =>
                                    (dropdownButtonRefs.current[
                                      `file-${file.id}`
                                    ] = el)
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDropdownOpen(
                                      dropdownOpen === `file-${file.id}`
                                        ? null
                                        : `file-${file.id}`
                                    );
                                  }}
                                  className="p-1 rounded-md bg-white shadow-md hover:bg-gray-50"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>

                                <DropdownMenu
                                  isOpen={dropdownOpen === `file-${file.id}`}
                                  onClose={() => setDropdownOpen(null)}
                                  buttonRef={
                                    dropdownButtonRefs.current[
                                      `file-${file.id}`
                                    ]
                                      ? {
                                          current:
                                            dropdownButtonRefs.current[
                                              `file-${file.id}`
                                            ],
                                        }
                                      : null
                                  }
                                >
                                  <button
                                    onClick={() => handleFileDownload(file)}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    다운로드
                                  </button>
                                  <button
                                    onClick={() => {
                                      setRenameTarget(file);
                                      setRenameForm({ name: file.file_name });
                                      setShowRenameFileModal(true);
                                      setDropdownOpen(null);
                                    }}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                  >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    이름 변경
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFile(file)}
                                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    삭제
                                  </button>
                                </DropdownMenu>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            이름
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            유형
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            크기
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            수정일
                          </th>
                          <th className="relative px-6 py-3">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {folders.map((folder) => (
                          <tr key={folder.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleFolderClick(folder)}
                                className="flex items-center gap-3 text-sm font-medium text-gray-900 hover:text-blue-600"
                              >
                                <Folder className="w-5 h-5 text-blue-500" />
                                {folder.folder_name}
                                {folder.folder_type === "share" && (
                                  <Globe className="w-4 h-4 text-green-600" />
                                )}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              폴더
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              -
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(folder.updated_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                ref={(el) =>
                                  (dropdownButtonRefs.current[
                                    `folder-list-${folder.id}`
                                  ] = el)
                                }
                                onClick={() =>
                                  setDropdownOpen(
                                    dropdownOpen === `folder-list-${folder.id}`
                                      ? null
                                      : `folder-list-${folder.id}`
                                  )
                                }
                                className="text-gray-400 hover:text-gray-600 p-2"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>

                              <DropdownMenu
                                isOpen={
                                  dropdownOpen === `folder-list-${folder.id}`
                                }
                                onClose={() => setDropdownOpen(null)}
                                buttonRef={
                                  dropdownButtonRefs.current[
                                    `folder-list-${folder.id}`
                                  ]
                                    ? {
                                        current:
                                          dropdownButtonRefs.current[
                                            `folder-list-${folder.id}`
                                          ],
                                      }
                                    : null
                                }
                              >
                                {folder.folder_type === "share" && (
                                  <>
                                    <button
                                      onClick={async () => {
                                        await loadInvitedMembers(folder.id);
                                        setSelectedFolderForInvite(folder);
                                        setShowInviteModal(true);
                                        setDropdownOpen(null);
                                      }}
                                      className="flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full text-left"
                                    >
                                      <Users className="w-4 h-4 mr-2" />
                                      멤버 초대
                                    </button>
                                    <button
                                      onClick={async () => {
                                        await loadInvitedMembers(folder.id);
                                        setSelectedFolderForBan(folder);
                                        setShowBanModal(true);
                                        setDropdownOpen(null);
                                      }}
                                      className="flex items-center px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 w-full text-left"
                                    >
                                      <UserX className="w-4 h-4 mr-2" />
                                      멤버 추방
                                    </button>
                                    <div className="border-t border-gray-100 my-1" />
                                  </>
                                )}
                                <button
                                  onClick={() => {
                                    setRenameTarget(folder);
                                    setRenameForm({ name: folder.folder_name });
                                    setShowRenameFolderModal(true);
                                    setDropdownOpen(null);
                                  }}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                >
                                  <Edit3 className="w-4 h-4 mr-2" />
                                  이름 변경
                                </button>
                                <button
                                  onClick={() => handleDeleteFolder(folder)}
                                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  삭제
                                </button>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}

                        {files.map((file) => (
                          <tr key={file.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleFileView(file)}
                                className="flex items-center gap-3 text-sm font-medium text-gray-900 hover:text-blue-600"
                              >
                                <SimpleFileIcon
                                  type={file.file_type}
                                  size={20}
                                  className="flex-shrink-0"
                                />
                                {file.file_name}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {file.file_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatFileSize(file.file_size)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(file.updated_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                ref={(el) =>
                                  (dropdownButtonRefs.current[
                                    `file-list-${file.id}`
                                  ] = el)
                                }
                                onClick={() =>
                                  setDropdownOpen(
                                    dropdownOpen === `file-list-${file.id}`
                                      ? null
                                      : `file-list-${file.id}`
                                  )
                                }
                                className="text-gray-400 hover:text-gray-600 p-2"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>

                              <DropdownMenu
                                isOpen={dropdownOpen === `file-list-${file.id}`}
                                onClose={() => setDropdownOpen(null)}
                                buttonRef={
                                  dropdownButtonRefs.current[
                                    `file-list-${file.id}`
                                  ]
                                    ? {
                                        current:
                                          dropdownButtonRefs.current[
                                            `file-list-${file.id}`
                                          ],
                                      }
                                    : null
                                }
                              >
                                <button
                                  onClick={() => handleFileDownload(file)}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  다운로드
                                </button>
                                <button
                                  onClick={() => {
                                    setRenameTarget(file);
                                    setRenameForm({ name: file.file_name });
                                    setShowRenameFileModal(true);
                                    setDropdownOpen(null);
                                  }}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                                >
                                  <Edit3 className="w-4 h-4 mr-2" />
                                  이름 변경
                                </button>
                                <button
                                  onClick={() => handleDeleteFile(file)}
                                  className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  삭제
                                </button>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 모달들 */}
        <CreateFolderModal
          open={showCreateFolderModal}
          onClose={() => {
            setShowCreateFolderModal(false);
            setCreateFolderForm({ folder_name: "", folder_type: "private" });
          }}
          form={createFolderForm}
          onChange={setCreateFolderForm}
          onSubmit={handleCreateFolder}
        />

        <RenameFolderModal
          open={showRenameFolderModal}
          onClose={() => {
            setShowRenameFolderModal(false);
            setRenameTarget(null);
            setRenameForm({ name: "" });
          }}
          form={renameForm}
          onChange={setRenameForm}
          onSubmit={handleRenameFolder}
        />

        <RenameFileModal
          open={showRenameFileModal}
          onClose={() => {
            setShowRenameFileModal(false);
            setRenameTarget(null);
            setRenameForm({ name: "" });
          }}
          form={renameForm}
          onChange={setRenameForm}
          onSubmit={handleRenameFile}
        />

        <UploadModal
          open={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadingFiles([]);
          }}
          files={uploadingFiles}
          onFilesChange={setUploadingFiles}
          onUpload={handleFileUpload}
          isRootFolder={!currentFolderId}
        />

        <SearchModal
          open={showSearchModal}
          onClose={() => {
            setShowSearchModal(false);
            setSearchQuery("");
            setSearchResults(null);
          }}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onSearch={handleSearch}
          results={searchResults}
          onSelectFile={handleFileView}
          onSelectFolder={handleFolderClick}
        />

        <PermissionModal
          open={showPermissionModal}
          onClose={() => {
            setShowPermissionModal(false);
            setPermissions([]);
          }}
          permissions={permissions}
          loading={isLoadingPermissions}
          onLoad={loadPermissions}
          onUpdate={handlePermissionUpdate}
        />

        <FileViewerModal
          open={showFileViewerModal}
          onClose={() => {
            setShowFileViewerModal(false);
            setViewingFile(null);
          }}
          file={viewingFile}
          viewFile={viewFile}
        />

        <InviteFolderModal
          open={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedFolderForInvite(null);
            setInvitedMembers([]);
          }}
          selectedFolder={selectedFolderForInvite}
          members={iamMembers || []}
          currentUserId={currentUser?.user_id}
          onInvite={handleInviteFolder}
          invitedMembers={invitedMembers}
          sharedFolders={folders.filter((f) => f.folder_type === "share")}
          sharedFiles={files}
        />

        <BanFolderModal
          open={showBanModal}
          onClose={() => {
            setShowBanModal(false);
            setSelectedFolderForBan(null);
            setInvitedMembers([]);
          }}
          folder={selectedFolderForBan}
          invitedMembers={invitedMembers}
          currentUser={currentUser}
          onBan={handleBanFolder}
          loading={isLoadingInvites}
          sharedFolders={folders.filter((f) => f.folder_type === "share")}
          sharedFiles={files}
        />
      </div>
    </>
  );
};

export default EDriveDashboard;
